import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  LinearProgress,
  Autocomplete,
} from "@mui/material";
import {
  Edit,
  Close,
  CloudUpload,
  Email,
  Phone,
  LocationOn,
  Language,
  Business,
} from "@mui/icons-material";
import api from "../../api/api";
import CustomSnackbar from "../../components/CustomSnackbar";
import en_ja from "../../en-ja-map";

const DEFAULT_COORDS = { lat: 35.6762, lng: 139.6503 };

const ProfilePage = () => {
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [clinicData, setClinicData] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");

  const [predictions, setPredictions] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState("");
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const geocoder = useRef(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);

  const [formData, setFormData] = useState({
    clinicName: "",
    email: "",
    clinicAddress: "",
    phoneNumber: "",
    landlineNumber: "",
    website: "",
    about: "",
    coordinates: DEFAULT_COORDS,
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchClinicData();
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarType("success");
  };

  const fetchClinicData = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (!user?.id) {
        console.error("No user ID found");
        return;
      }

      const response = await api.get(`/v1/clinic/${user.id}`);
      // Handle the response structure for fetch API (assuming it returns the clinic data directly or in a data field)
      const clinic = response.data.data || response.data;

      if (clinic) {
        setClinicData(clinic);
        const coords =
          clinic.latitude && clinic.longitude
            ? { lat: Number(clinic.latitude), lng: Number(clinic.longitude) }
            : DEFAULT_COORDS;
        setFormData({
          clinicName: clinic.clinic_name || "",
          email: clinic.email || "",
          clinicAddress: clinic.clinic_location || "",
          phoneNumber: clinic.phone_no || "",
          landlineNumber: clinic.landline_no || "",
          website: clinic.website || "",
          about: clinic.about || "",
          coordinates: coords,
        });
        if (clinic.profile_image) {
          setProfileImage(clinic.profile_image);
          setPreviewImage(clinic.profile_image);
        }
      }
    } catch (error) {
      console.error("Error fetching clinic data:", error);
      setSnackbarMessage(en_ja["Failed to load clinic data"]);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleMaps = () => {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.Map) {
        setMapLoaded(true);
        resolve();
        return;
      }

      const existingScript = document.querySelector(
        'script[src*="maps.googleapis.com"]'
      );
      if (existingScript) {
        const checkGoogleMapsReady = () => {
          if (window.google && window.google.maps && window.google.maps.Map) {
            setMapLoaded(true);
            resolve();
          } else {
            setTimeout(checkGoogleMapsReady, 100);
          }
        };
        checkGoogleMapsReady();
        return;
      }

      const script = document.createElement("script");
      script.src = script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD-206GuB2zh4Pl6c2EEf4c1U0Qw-ju9kY&libraries=places&language=ja&region=JP&callback=initGoogleMaps`;;
      script.async = true;
      script.defer = true;

      window.initGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.Map) {
          setMapLoaded(true);
          resolve();
        } else {
          setMapError(en_ja["Google Maps failed to initialize properly"]);
          reject(new Error("Google Maps failed to initialize"));
        }
      };

      script.onerror = () => {
        setMapError(en_ja["Failed to load Google Maps script"]);
        reject(new Error("Failed to load Google Maps script"));
      };

      document.head.appendChild(script);
    });
  };

  const initializeMap = (centerCoords = formData.coordinates) => {
    if (
      !window.google ||
      !window.google.maps ||
      !window.google.maps.Map ||
      !mapRef.current
    ) {
      return;
    }

    try {
      if (mapInstance.current && markerInstance.current) {
        const latlng = new window.google.maps.LatLng(
          centerCoords.lat,
          centerCoords.lng
        );
        mapInstance.current.setCenter(latlng);
        markerInstance.current.setPosition(latlng);
        return;
      }

      const centerLatLng = new window.google.maps.LatLng(
        centerCoords.lat,
        centerCoords.lng
      );
      const map = new window.google.maps.Map(mapRef.current, {
        center: centerLatLng,
        zoom: 13,
      });

      const marker = new window.google.maps.Marker({
        position: centerLatLng,
        map,
        draggable: true,
      });

      map.addListener("click", (e) => {
        if (e.latLng) {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          marker.setPosition(e.latLng);
          setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }));
          setSelectedPlaceId(null);
          setAddressSelectedFromDropdown(true);

          if (geocoder.current) {
            geocoder.current.geocode(
              { location: { lat, lng } },
              (results, status) => {
                if (status === "OK" && results && results[0]) {
                  setFormData((prev) => ({
                    ...prev,
                    clinicAddress: results[0].formatted_address,
                  }));
                  setAddressSelectedFromDropdown(true);
                }
              }
            );
          }
        }
      });

      marker.addListener("dragend", (e) => {
        if (e.latLng) {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          setFormData((prev) => ({ ...prev, coordinates: { lat, lng } }));
          setSelectedPlaceId(null);
          setAddressSelectedFromDropdown(true);

          if (geocoder.current) {
            geocoder.current.geocode(
              { location: { lat, lng } },
              (results, status) => {
                if (status === "OK" && results && results[0]) {
                  setFormData((prev) => ({
                    ...prev,
                    clinicAddress: results[0].formatted_address,
                  }));
                  setAddressSelectedFromDropdown(true);
                }
              }
            );
          }
        }
      });

      mapInstance.current = map;
      markerInstance.current = marker;

      // Initialize services after map is created
      geocoder.current = new window.google.maps.Geocoder();
      if (
        window.google.maps.places &&
        window.google.maps.places.AutocompleteService
      ) {
        autocompleteService.current =
          new window.google.maps.places.AutocompleteService();
        placesService.current = new window.google.maps.places.PlacesService(
          map
        );
        console.log("Google Maps services initialized successfully");
      }

      setTimeout(() => {
        if (mapInstance.current) {
          window.google.maps.event.trigger(mapInstance.current, "resize");
          mapInstance.current.setCenter(centerLatLng);
        }
      }, 300);
    } catch (error) {
      console.error("Error initializing map:", error);
      setMapError(en_ja["Failed to initialize map"] + ": " + error.message);
    }
  };

  const handleSelectPrediction = (placeId, description) => {
    setSelectedPlaceId(placeId);
    setAddressSelectedFromDropdown(true);
    setFormData((prev) => ({ ...prev, clinicAddress: description }));
    setPredictions([]);
    if (formErrors.clinicAddress) {
      setFormErrors((prev) => ({ ...prev, clinicAddress: "" }));
    }

    if (!placesService.current) return;

    placesService.current.getDetails({ placeId }, (place, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        place.geometry &&
        place.geometry.location
      ) {
        const location = place.geometry.location;
        setFormData((prev) => ({
          ...prev,
          coordinates: { lat: location.lat(), lng: location.lng() },
          clinicAddress: place.formatted_address,
        }));
      }
    });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.clinicName.trim())
      errors.clinicName = en_ja["Clinic name is required"];
    if (!formData.email.trim()) {
      errors.email = en_ja["Email is required"];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = en_ja["Invalid email format"];
    }
    if (!formData.phoneNumber.trim())
      errors.phoneNumber = en_ja["Phone number is required"];
    if (!formData.clinicAddress.trim()) {
      errors.clinicAddress = en_ja["Clinic address is required"];
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: "" });
    }
    if (field === "clinicAddress") {
      setSelectedPlaceId(null);
      setAddressSelectedFromDropdown(true);
    }
  };

  const handleEditProfile = async () => {
    try {
      await loadGoogleMaps();
      setAddressSelectedFromDropdown(true); // Set to true for existing address
      setEditDialogOpen(true);
      setTimeout(() => {
        if (mapRef.current) {
          initializeMap(formData.coordinates);
        }
      }, 500);
    } catch (error) {
      console.error("Failed to load Google Maps:", error);
      setMapError(en_ja["Failed to load Google Maps"]);
      setEditDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setFormErrors({});
    setSelectedPlaceId(null);
    setAddressSelectedFromDropdown(true); // Reset to true for existing data
    setProfileImageFile(null); // Reset new image file
    setPreviewImage(clinicData?.profile_image || null);
    if (markerInstance.current) {
      markerInstance.current.setMap(null);
      markerInstance.current = null;
    }
    if (mapInstance.current) {
      mapInstance.current = null;
    }
    setMapLoaded(false);
    setMapError("");
    // Reset form data to original values
    const coords =
      clinicData.latitude && clinicData.longitude
        ? {
          lat: Number(clinicData.latitude),
          lng: Number(clinicData.longitude),
        }
        : DEFAULT_COORDS;
    setFormData({
      clinicName: clinicData?.clinic_name || "",
      email: clinicData?.email || "",
      clinicAddress: clinicData?.clinic_location || "",
      phoneNumber: clinicData?.phone_no || "",
      landlineNumber: clinicData?.landline_no || "",
      website: clinicData?.website || "",
      about: clinicData?.about || "",
      coordinates: coords,
    });
    setProfileImage(clinicData?.profile_image || null);
  };

  const handleUpdateProfile = async () => {
    if (!validateForm()) return;

    setUpdateLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      if (!user?.id) {
        throw new Error("User not found");
      }

      const formDataToSend = new FormData();
      formDataToSend.append("clinic_name", formData.clinicName);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("clinic_location", formData.clinicAddress);
      formDataToSend.append("phone_no", formData.phoneNumber);
      formDataToSend.append("landline_no", formData.landlineNumber || "");
      formDataToSend.append("website", formData.website || ""); // ADD THIS
      formDataToSend.append("about", formData.about || "");
      formDataToSend.append("latitude", formData.coordinates.lat.toString());
      formDataToSend.append("longitude", formData.coordinates.lng.toString());

      if (profileImageFile) {
        formDataToSend.append("profile_image", profileImageFile);
      }

      const response = await api.put(
        `/v1/clinic/update-profile/${user.id}`,
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      console.log(response, "Update response");

      // More robust success check
      if (response.status === 200 || response.data?.success !== false) {
        setSnackbarMessage(
          response.data?.message || en_ja["Profile updated successfully!"]
        );
        setSnackbarType("success");
        setSnackbarOpen(true);

        // Close dialog first
        setEditDialogOpen(false);

        // Fetch updated data
        await fetchClinicData();

        // Update localStorage with merged data
        const updatedUser = {
          ...user,
          clinic_name: formData.clinicName,
          phone_no: formData.phoneNumber,
          landline_no: formData.landlineNumber,
          clinic_location: formData.clinicAddress,
          website: formData.website, // ADD THIS
          about: formData.about,
          profile_image: previewImage || user.profile_image,
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        // Optional: Reload after a delay to ensure state updates
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating profile:", error);

      // More specific error handling
      let errorMessage = en_ja["Failed to update profile. Please try again."];

      if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || en_ja["Invalid input data"];
      } else if (error.response?.status === 401) {
        errorMessage = en_ja["Session expired. Please login again"];
        // Optionally redirect to login
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setUpdateLoading(false);
    }
  };

  const generateClinicAvatar = (name) => {
    const nameParts = name?.split(" ");
    if (nameParts?.length >= 2) {
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    }
    return name?.substring(0, 2).toUpperCase();
  };

  const handleImageChange = (file) => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
      setProfileImageFile(file);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const onFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleImageChange(files[0]);
  };

  const onFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleImageChange(file);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: "center" }}>
          {en_ja["Loading profile..."]}
        </Typography>
      </Box>
    );
  }

  if (!clinicData) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          {en_ja["Failed to load clinic data"]}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: "100%",
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <CustomSnackbar
        isOpen={snackbarOpen}
        message={snackbarMessage}
        onClose={handleCloseSnackbar}
        type={snackbarType}
      />

      <Paper elevation={0} sx={{ borderRadius: "16px", overflow: "hidden" }}>
        <Box
          sx={{
            p: 4,
            background: "linear-gradient(135deg, #fafbfc 0%, #ffffff 100%)",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  fontSize: "28px",
                  fontWeight: "600",
                  backgroundColor: "#76AEDE",
                  color: "white",
                  boxShadow: "0 8px 24px rgba(118, 174, 222, 0.3)",
                  border: "4px solid white",
                }}
                src={clinicData?.profile_image}
              >
                {!clinicData?.profile_image &&
                  generateClinicAvatar(clinicData?.clinic_name)}
              </Avatar>
              {/* <IconButton
                onClick={handleEditProfile}
                sx={{
                  position: "absolute",
                  bottom: -8,
                  right: -8,
                  backgroundColor: "white",
                  color: "#76AEDE",
                  border: "2px solid #76AEDE",
                  width: 40,
                  height: 40,
                  "&:hover": {
                    backgroundColor: "#76AEDE",
                    color: "white",
                    transform: "scale(1.05)",
                  },
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  transition: "all 0.2s ease",
                }}
              >
                <Edit fontSize="small" />
              </IconButton> */}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Typography
                  variant="h4"
                  fontWeight="700"
                  color="text.primary"
                  sx={{ fontSize: "28px" }}
                >
                  {clinicData?.clinic_name}
                </Typography>
              </Box>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, fontSize: "16px", lineHeight: 1.6 }}
              >
                {clinicData?.about || en_ja["No description available"]}
              </Typography>

              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEditProfile}
                sx={{
                  borderColor: "#76AEDE",
                  color: "#76AEDE",
                  "&:hover": {
                    backgroundColor: "#76AEDE",
                    color: "white",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(118, 174, 222, 0.3)",
                  },
                  transition: "all 0.2s ease",
                  px: 3,
                  py: 1,
                }}
              >
                {en_ja["Edit Profile"]}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <Email sx={{ color: "#76AEDE", fontSize: "20px" }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "12px", fontWeight: "500" }}
                  >
                    {en_ja["Email"]}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{ fontWeight: "500" }}
                  >
                    {clinicData?.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
              >
                <Phone sx={{ color: "#76AEDE", fontSize: "20px" }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "12px", fontWeight: "500" }}
                  >
                    {en_ja["Phone Number"]}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{ fontWeight: "500" }}
                  >
                    {clinicData?.phone_no}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {clinicData?.landline_no && (
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Phone sx={{ color: "#76AEDE", fontSize: "20px" }} />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      {en_ja["Landline Number"]}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.primary"
                      sx={{ fontWeight: "500" }}
                    >
                      {clinicData?.landline_no}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  mb: 3,
                }}
              >
                <LocationOn
                  sx={{ color: "#76AEDE", fontSize: "20px", mt: 0.5 }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "12px", fontWeight: "500" }}
                  >
                    {en_ja["Address"]}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.primary"
                    sx={{ fontWeight: "500", lineHeight: 1.5 }}
                  >
                    {clinicData?.clinic_location}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {clinicData?.website && (
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Language sx={{ color: "#76AEDE", fontSize: "20px" }} />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      {en_ja["Website"]}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.primary"
                      sx={{ fontWeight: "500" }}
                    >
                      {clinicData?.website}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{ pb: 2, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" fontWeight="600">
              {en_ja["Edit Profile"]}
            </Typography>
            <IconButton
              onClick={handleCloseDialog}
              sx={{ color: "text.secondary" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 3,
            paddingTop: "24px !important",
            height: "70vh",
            display: "flex",
            p: 0,
          }}
        >
          <Box
            sx={{
              width: 400,
              px: 3,
              borderRight: 1,
              borderColor: "divider",
              overflowY: "auto",
              pt: 2,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "flex", gap: 3 }}>
                <TextField
                  label={en_ja["Clinic Name"]}
                  value={formData.clinicName}
                  onChange={(e) =>
                    handleFieldChange("clinicName", e.target.value)
                  }
                  error={!!formErrors.clinicName}
                  helperText={formErrors.clinicName}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={en_ja["Email"]}
                  value={formData.email}
                  onChange={(e) => handleFieldChange("email", e.target.value)}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  sx={{ flex: 1 }}
                  disabled
                />
              </Box>

              <Box sx={{ display: "flex", gap: 3 }}>
                <TextField
                  label={en_ja["Phone No."]}
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    handleFieldChange("phoneNumber", e.target.value)
                  }
                  error={!!formErrors.phoneNumber}
                  helperText={formErrors.phoneNumber}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={en_ja["Landline No."]}
                  value={formData.landlineNumber}
                  onChange={(e) =>
                    handleFieldChange("landlineNumber", e.target.value)
                  }
                  sx={{ flex: 1 }}
                />
              </Box>

              <Autocomplete
                freeSolo
                options={predictions.map((p) => ({
                  label: p.description,
                  placeId: p.place_id,
                }))}
                value={formData.clinicAddress || ""}
                onInputChange={(event, value) => {
                  setFormData((prev) => ({
                    ...prev,
                    clinicAddress: value || "",
                  }));
                  setFormErrors((prev) => ({ ...prev, clinicAddress: "" }));
                  setSelectedPlaceId(null);
                  setAddressSelectedFromDropdown(true);

                  if (!value || !autocompleteService.current) {
                    setPredictions([]);
                    return;
                  }

                  console.log("Searching for predictions:", value);
                  autocompleteService.current.getPlacePredictions(
                    { input: value, types: ["establishment", "geocode"] },
                    (results, status) => {
                      console.log("Prediction results:", results, status);
                      if (
                        status ===
                        window.google.maps.places.PlacesServiceStatus.OK
                      ) {
                        setPredictions(results || []);
                      } else {
                        setPredictions([]);
                      }
                    }
                  );
                }}
                onChange={(event, newValue) => {
                  if (typeof newValue === "string") {
                    setFormData((prev) => ({
                      ...prev,
                      clinicAddress: newValue,
                    }));
                    setSelectedPlaceId(null);
                    setAddressSelectedFromDropdown(true);
                    setFormErrors((prev) => ({ ...prev, clinicAddress: "" }));
                  } else if (newValue && newValue.placeId) {
                    console.log("Selected prediction:", newValue);
                    handleSelectPrediction(newValue.placeId, newValue.label);
                  } else {
                    setFormData((prev) => ({ ...prev, clinicAddress: "" }));
                    setSelectedPlaceId(null);
                    setAddressSelectedFromDropdown(true);
                  }
                  setPredictions([]);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={en_ja["Clinic Address"]}
                    error={!!formErrors.clinicAddress}
                    helperText={formErrors.clinicAddress}
                    multiline
                    rows={2}
                  />
                )}
              />

              <TextField
                label={en_ja["Website"]}
                value={formData.website}
                onChange={(e) => handleFieldChange("website", e.target.value)}
                fullWidth
              />

              <TextField
                label={en_ja["About Clinic"]}
                value={formData.about}
                onChange={(e) => handleFieldChange("about", e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={en_ja["Tell us about your clinic..."]}
              />

              <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
                  {en_ja["Upload Image"]}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {en_ja["Upload Image for the Clinic"]}
                </Typography>

                {previewImage && (
                  <Box sx={{ mb: 2, textAlign: "center" }}>
                    <Avatar
                      src={previewImage}
                      sx={{ width: 80, height: 80, mx: "auto", mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {en_ja["Current Image"]}
                    </Typography>
                  </Box>
                )}

                <Box
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onFileDrop}
                  sx={{
                    border: "2px dashed",
                    borderColor: dragOver ? "#76AEDE" : "#e0e0e0",
                    borderRadius: "12px",
                    p: 6,
                    textAlign: "center",
                    backgroundColor: dragOver
                      ? "rgba(118, 174, 222, 0.05)"
                      : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudUpload
                    sx={{
                      fontSize: 48,
                      color: dragOver ? "#76AEDE" : "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {en_ja["Drag and drop the image"]}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {en_ja["or"]}
                  </Typography>
                  <Button
                    variant="text"
                    sx={{
                      color: "#76AEDE",
                      textTransform: "none",
                      fontSize: "14px",
                    }}
                  >
                    {en_ja["Choose a Image"]}
                  </Button>
                </Box>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onFileInput}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, position: "relative" }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">
                {en_ja["Click on the map or drag the marker to set the clinic location"]}
              </Typography>
            </Box>

            <Box sx={{ height: "calc(100% - 60px)", position: "relative" }}>
              {mapError ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    flexDirection: "column",
                  }}
                >
                  <Typography color="error" gutterBottom>
                    {mapError}
                  </Typography>
                  <Button
                    onClick={() => {
                      setMapError("");
                      loadGoogleMaps().then(() =>
                        initializeMap(formData.coordinates)
                      );
                    }}
                  >
                    {en_ja["Retry"]}
                  </Button>
                </Box>
              ) : !mapLoaded ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography>{en_ja["Loading Google Maps..."]}</Typography>
                </Box>
              ) : (
                <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
              )}

              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  bgcolor: "background.paper",
                  p: 1,
                  borderRadius: 1,
                  boxShadow: 1,
                  fontSize: "0.75rem",
                }}
              >
                <strong>{en_ja["Coordinates:"]} </strong>
                <br />
                {formData.coordinates.lat.toFixed(6)},{" "}
                {formData.coordinates.lng.toFixed(6)}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: "text.secondary",
              "&:hover": { backgroundColor: "error.light", color: "white" },
            }}
          >
            {en_ja["Cancel"]}
          </Button>
          <Button
            onClick={handleUpdateProfile}
            variant="contained"
            sx={{
              backgroundColor: "#ef5350",
              "&:hover": { backgroundColor: "#d32f2f" },
              px: 4,
            }}
            disabled={updateLoading}
          >
            {updateLoading ? en_ja["Updating..."] : en_ja["Update profile"]}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;